import { useFormik } from 'formik';
import { Button, Dialog, FormLabel, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, Radio, RadioGroup, TextField } from '@mui/material';
import { Publisher, ServiceAssignment } from '../../../domain/Publisher';
import { observer } from 'mobx-react-lite';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import moment from 'moment';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { FieldServiceReport } from '../../../domain/FieldServiceReport';
import { useTranslation } from "react-i18next";
import * as yup from 'yup';
import { getLocale } from '../../../utils/localeUtils';

interface Props {
    isOpen: boolean;
    publisher: Publisher;
    report?: FieldServiceReport;
    onSubmit: (report: FieldServiceReport) => void;
    onClose: () => void;
}

export default observer((props: Props) => {
    const { t } = useTranslation();

    let formData = {
        date: moment().subtract(1, 'month').format('YYYY-MM-DD'),
        empty: false,
    } as FieldServiceReport;
    if (props.report) {
        formData = {
            ...formData,
            ...props.report
        }
    }
    if (!formData.serviceAssignment) {
        formData.serviceAssignment = undefined;
    }

    const schema = yup.object().shape({
        date: yup.date().required(t("field_is_required", { name: t("date") })),
    });
    const formik = useFormik({
        validationSchema: schema,
        enableReinitialize: true,
        initialValues: formData,
        onSubmit: (report, { resetForm }) => {
            const updatedReport = {
                ...report,
                publisherId: props.publisher.id,
                hours: report.hours || 0,
                bibleStudies: report.bibleStudies || 0,
                empty: report.empty
            } as FieldServiceReport;
            if (updatedReport.serviceAssignment === undefined) {
                delete updatedReport.serviceAssignment;
            }
            props.onSubmit(updatedReport);
            resetForm();
        }
    });

    const { errors, values } = formik;
    return (
        <Dialog open={props.isOpen} onClose={props.onClose}>
            <DialogTitle>{t("field_service_report")}</DialogTitle>
            <form onSubmit={formik.handleSubmit}>
                <DialogContent>
                    <LocalizationProvider dateAdapter={AdapterDateFns} locale={getLocale()}>
                        <DatePicker
                            label={t("date")}
                            views={['year', 'month']}
                            maxDate={new Date()}
                            value={values.date}
                            onChange={value => {
                                formik.setFieldValue('date', value ? moment(value).format('YYYY-MM-DD') : value);
                            }}
                            renderInput={(params) =>
                                <TextField
                                    {...params}
                                    error={!!errors.date}
                                    helperText={errors.date && errors.date as string}
                                    margin="normal"
                                    name="date"
                                />}
                        />
                    </LocalizationProvider>
                    <Grid container>
                        {values.serviceAssignment ?
                            <Grid item xs={6} padding={1}>
                                <TextField
                                    margin="normal"
                                    id="hours"
                                    label={t("hours")}
                                    type="number"
                                    variant="standard"
                                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                    value={values.hours || ''}
                                    onChange={formik.handleChange}
                                /></Grid>
                            :
                            <Grid item xs={6} padding={1}>
                                <FormLabel>{t("was_preaching")}</FormLabel>
                                <RadioGroup
                                    row
                                    id="empty"
                                    value={values.empty}
                                    onChange={(e, v) => {
                                        formik.setFieldValue('empty', v === "true");
                                    }}
                                >
                                    <FormControlLabel
                                        value={false}
                                        control={<Radio />}
                                        checked={!values.empty}
                                        label={t("yes")} />
                                    <FormControlLabel
                                        value={true}
                                        checked={values.empty}
                                        control={<Radio />}
                                        label={t("no")} />
                                </RadioGroup>
                            </Grid>}
                        <Grid item xs={6} padding={1}>
                            {!values.empty && <TextField
                                margin="normal"
                                id="bibleStudies"
                                label={t("bible_studies")}
                                type="number"
                                variant="standard"
                                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                value={values.bibleStudies || ''}
                                onChange={formik.handleChange}
                            />}
                        </Grid>
                    </Grid>


                    <TextField
                        margin="normal"
                        id="notes"
                        label={t("notes")}
                        type="text"
                        fullWidth
                        variant="standard"
                        value={values.notes || ''}
                        onChange={formik.handleChange}
                    />

                    <Grid container>
                        <Grid item xs={6} padding={1}>
                            <FormLabel>{t("service_assignment")}</FormLabel>
                            <RadioGroup
                                name="serviceAssignment"
                                value={values.serviceAssignment}
                                onChange={(e, v) => {
                                    formik.setFieldValue('empty', false);
                                    formik.setFieldValue('serviceAssignment', v);
                                    const notes = v === ServiceAssignment.AuxiliaryPioneer ? t("auxiliary_pioneer_short") : "";
                                    formik.setFieldValue('notes', notes);
                                }}
                            >
                                {Object.values(ServiceAssignment)
                                    .map(assign => (
                                        <FormControlLabel
                                            key={assign}
                                            value={assign}
                                            checked={values.serviceAssignment && values.serviceAssignment === assign}
                                            control={<Radio />}
                                            label={t(assign)}
                                        />))}
                            </RadioGroup>
                        </Grid>
                        <Grid item xs={6} padding={1}>

                        </Grid>
                    </Grid>

                </DialogContent>
                <DialogActions>
                    <Button onClick={props.onClose}>{t("cancel")}</Button>
                    <Button type='submit'>{t("submit")}</Button>
                </DialogActions>
            </form>
        </Dialog>
    );
});